<div class="test-container">
  <!-- Loading State -->
  @if (isLoading()) {
    <div class="loading-screen">
      <div class="spinner-large"></div>
      <p class="loading-text">Cargando...</p>
    </div>
  }

  <!-- Pantalla inicial (antes de iniciar) -->
  @else if (!examenIniciado() && examen() && !mostrandoResultados()) {
    <div class="start-screen">
      <button class="btn-back" (click)="goBack()">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M8 16L2 10L8 4M2 10H18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Volver
      </button>

      <div class="start-card">
        <h1 class="exam-title">{{ examen()!.titulo }}</h1>
        <p class="exam-description">{{ examen()!.descripcion }}</p>

        <div class="exam-stats-grid">
          <div class="stat-box">
            <span class="stat-label">📋 Preguntas</span>
            <span class="stat-value">{{ examen()!.preguntas.length }}</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">⏱️ Tiempo límite</span>
            <span class="stat-value">{{ examen()!.tiempo_limite || 'Sin límite' }} min</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">🎯 Nota mínima</span>
            <span class="stat-value">{{ examen()!.nota_minima_aprobacion }}%</span>
          </div>
          <div class="stat-box">
            <span class="stat-label">🔄 Intentos</span>
            <span class="stat-value">{{ examen()!.intentos_permitidos === 0 ? '∞' : examen()!.intentos_permitidos }}</span>
          </div>
        </div>

        <button class="btn-start" (click)="iniciarExamen()">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 3L15 10L5 17V3Z" fill="currentColor"/>
          </svg>
          Iniciar Examen
        </button>
      </div>
    </div>
  }

  <!-- Vista del examen en progreso -->
  @else if (examenIniciado() && examen() && !mostrandoResultados()) {
    <div class="exam-viewer">
      <!-- Header fijo con temporizador -->
      <div class="exam-header-bar">
        <div class="exam-info">
          <h1 class="exam-title-small">{{ examen()!.titulo }}</h1>
          <p class="progress-text">{{ getPreguntasRespondidas() }} de {{ examen()!.preguntas.length }} respondidas</p>
        </div>

        <div class="timer-display">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2"/>
            <path d="M10 5V10L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <span class="timer-text">{{ formatTime(tiempoTranscurrido()) }}</span>
        </div>
      </div>

      <!-- Lista de preguntas con opciones para responder -->
      <div class="preguntas-scroll">
        @for (pregunta of examen()!.preguntas; track pregunta.id_pregunta; let i = $index) {
          <div class="pregunta-card">
            <div class="pregunta-header">
              <div class="pregunta-number">#{{ i + 1 }}</div>
              <div class="pregunta-meta">
                <span class="pregunta-puntos">{{ pregunta.puntos }} pts</span>
                @if (pregunta.obligatoria) {
                  <span class="badge-obligatoria">Obligatoria</span>
                }
              </div>
            </div>

            <div class="pregunta-texto">
              {{ pregunta.texto_pregunta }}
            </div>

            @if (pregunta.imagen_url) {
              <div class="pregunta-imagen">
                <img [src]="pregunta.imagen_url" alt="Imagen de la pregunta">
              </div>
            }

            <!-- Opciones de respuesta según el tipo -->
            <div class="respuestas-container">
              @if (pregunta.tipo_pregunta === 'opcion_multiple' || pregunta.tipo_pregunta === 'verdadero_falso' || pregunta.tipo_pregunta === 'seleccion_multiple') {
                @for (opcion of pregunta.opciones; track opcion.id_opcion) {
                  <label class="opcion-label" [class.selected]="getRespuesta(pregunta.id_pregunta)?.id_opcion === opcion.id_opcion">
                    <input 
                      type="radio" 
                      [name]="'pregunta_' + pregunta.id_pregunta"
                      [value]="opcion.id_opcion"
                      [checked]="getRespuesta(pregunta.id_pregunta)?.id_opcion === opcion.id_opcion"
                      (change)="guardarRespuesta(pregunta, opcion.id_opcion)">
                    <span class="opcion-texto">{{ opcion.texto_opcion }}</span>
                    <span class="checkmark"></span>
                  </label>
                }
              }

              @if (pregunta.tipo_pregunta === 'respuesta_corta') {
                <textarea 
                  class="text-answer"
                  rows="3"
                  placeholder="Escribe tu respuesta aquí..."
                  [value]="getRespuesta(pregunta.id_pregunta)?.respuesta_texto || ''"
                  (input)="guardarRespuesta(pregunta, undefined, $any($event.target).value)">
                </textarea>
              }

              @if (pregunta.tipo_pregunta === 'respuesta_larga') {
                <textarea 
                  class="text-answer large"
                  rows="6"
                  placeholder="Desarrolla tu respuesta de forma completa..."
                  [value]="getRespuesta(pregunta.id_pregunta)?.respuesta_texto || ''"
                  (input)="guardarRespuesta(pregunta, undefined, $any($event.target).value)">
                </textarea>
              }
            </div>
          </div>
        }

        <!-- Botón de finalizar al final -->
        <div class="finalizar-container">
          <button class="btn-finalizar" (click)="finalizarExamen()">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16 6L8 14L4 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            Finalizar Examen
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Pantalla de resultados -->
  @else if (mostrandoResultados() && resultados()) {
    <div class="results-screen">
      <div class="results-card">
        <div class="results-icon" [class.passed]="resultados().aprobado" [class.failed]="!resultados().aprobado">
          @if (resultados().aprobado) {
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="38" fill="#77d27a"/>
              <path d="M25 40L35 50L55 30" stroke="white" stroke-width="4" stroke-linecap="round"/>
            </svg>
          } @else {
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="38" fill="#DC3D1A"/>
              <path d="M30 30L50 50M50 30L30 50" stroke="white" stroke-width="4" stroke-linecap="round"/>
            </svg>
          }
        </div>

        <h1 class="results-title">
          @if (resultados().aprobado) {
            ¡Felicitaciones!
          } @else {
            No aprobaste
          }
        </h1>

        <p class="results-subtitle">
          @if (resultados().aprobado) {
            Has aprobado el examen exitosamente
          } @else {
            No alcanzaste la nota mínima requerida
          }
        </p>

        <div class="results-stats">
          <div class="result-stat">
            <span class="result-label">Puntuación</span>
            <span class="result-value">{{ resultados().porcentaje }}%</span>
          </div>
          <div class="result-stat">
            <span class="result-label">Puntos obtenidos</span>
            <span class="result-value">{{ resultados().puntos_obtenidos }} / {{ resultados().puntos_totales }}</span>
          </div>
          <div class="result-stat">
            <span class="result-label">Tiempo total</span>
            <span class="result-value">{{ formatTime(tiempoTranscurrido()) }}</span>
          </div>
        </div>

        <button class="btn-back-course" (click)="goBack()">
          Volver al Curso
        </button>
      </div>
    </div>
  }
</div>
